import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Sector,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useForestOwnership } from '../../../hooks/useForestOwnership';
import {
  FO_PALETTE,
  FO_PRIVATE_CLASSES,
  FO_PUBLIC_CLASSES,
} from '../../../lib/foColors';
import { FoClass } from '../../../lib/types';
import {
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
  SkeletonStatCardGrid,
} from './contentPrimitives';

interface ForestOwnershipContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

const fmtAcres = (n: number) =>
  n >= 1000 ? `${Math.round(n).toLocaleString()} ac` : `${n.toFixed(1)} ac`;
const fmtPct = (f: number) => `${(f * 100).toFixed(1)}%`;

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.forest_ownership} />
    <SkeletonPulseBlock className="h-[260px]" />
    <SkeletonStatCardGrid />
  </div>
);

// Expand the hovered slice slightly so highlight follows the arc rather than
// Recharts' bounding-box default. Matches SpeciesContent pattern.
const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="#FBFFEF"
      strokeWidth={1.5}
    />
  );
};

export const ForestOwnershipContent: React.FC<ForestOwnershipContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.forest_ownership;
  const { data, isLoading, error } = useForestOwnership(millCode, driveTimeMinutes);

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          Forest ownership drives how the shed is managed &mdash; harvest
          flexibility, restoration access, and supply continuity all flow from
          who owns the land. A supply shed that&rsquo;s mostly{' '}
          <strong>Family</strong> forest behaves very differently from one
          dominated by <strong>Federal</strong> lands or{' '}
          <strong>Corporate / TIMO</strong> (Timber Investment Management
          Organization) holdings.
        </p>
        <p>
          Classes come from the USFS Research Data Archive product{' '}
          <em>RDS-2025-0045</em> (Harris, Caputo &amp; Butler 2025) &mdash; a
          continuous 10&thinsp;m surface that fuses parcel-derived ownership
          boundaries with the National Land Cover Database to flag forested
          vs. non-forested pixels.
        </p>
        <p>
          Pie slices show share of <em>forested</em> acres by ownership type.
          Non-forest and water acres are reported separately as context (pie
          percentages would be dominated by non-forest in any mill-adjacent
          shed otherwise).
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.forestAcres > 0 && (() => {
        const totalShed = data.forestAcres + data.nonForestAcres + data.waterAcres;
        const nonForestFrac = totalShed > 0 ? data.nonForestAcres / totalShed : 0;
        const waterFrac     = totalShed > 0 ? data.waterAcres / totalShed     : 0;

        const slices = FO_PALETTE.order
          .map(cls => ({
            cls,
            name: FO_PALETTE.labels[cls],
            color: FO_PALETTE.colors[cls],
            acres: data.classAcres[cls],
            value: data.forestAcres > 0 ? (data.classAcres[cls] / data.forestAcres) * 100 : 0,
          }))
          .filter(s => s.acres > 0);

        const shareOf = (classes: FoClass[]) =>
          data.forestAcres > 0
            ? classes.reduce((s, c) => s + data.classAcres[c], 0) / data.forestAcres
            : 0;
        const privateFrac = shareOf(FO_PRIVATE_CLASSES);
        const publicFrac  = shareOf(FO_PUBLIC_CLASSES);
        const unknownFrac = shareOf(['unknown_forest']);

        const largest = slices.reduce(
          (top, s) => (s.acres > top.acres ? s : top),
          slices[0],
        );

        return (
          <>
            <LayerLiveBadge />

            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <h4 className="text-base font-roboto font-bold text-figma-text mb-4">
                Ownership composition <span className="text-neutral-500 font-normal">(% of forested acres)</span>
              </h4>
              <ResponsiveContainer width="100%" height={320} minWidth={0}>
                <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                  <Pie
                    data={slices}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={110}
                    paddingAngle={1}
                    isAnimationActive={false}
                    activeShape={renderActiveShape}
                  >
                    {slices.map(s => (
                      <Cell key={s.cls} fill={s.color} stroke="#1f2937" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                    itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                    wrapperStyle={{ zIndex: 50, outline: 'none' }}
                    formatter={(value: number | undefined, name: string | undefined, props: { payload?: { acres?: number } }) => {
                      const pct = value ?? 0;
                      const acres = props.payload?.acres ?? 0;
                      return [`${pct.toFixed(1)}%  (${fmtAcres(acres)})`, name ?? ''];
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                    wrapperStyle={{ fontSize: 12, fontFamily: 'Roboto, sans-serif', color: '#FBFFEF', paddingTop: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="pt-3 mt-2 border-t border-figma-lime/20 text-sm font-roboto text-neutral-300 leading-relaxed">
                <span className="font-roboto-mono text-figma-lime">{fmtPct(privateFrac)}</span>
                <span className="text-neutral-400"> private </span>
                <span className="text-neutral-500">(Family + Corporate)</span>
                <span className="text-neutral-600"> &middot; </span>
                <span className="font-roboto-mono text-figma-lime">{fmtPct(publicFrac)}</span>
                <span className="text-neutral-400"> public </span>
                <span className="text-neutral-500">(Federal, State, Local, Tribal)</span>
                {unknownFrac > 0.005 && (
                  <>
                    <span className="text-neutral-600"> &middot; </span>
                    <span className="font-roboto-mono text-neutral-400">{fmtPct(unknownFrac)}</span>
                    <span className="text-neutral-500"> unknown</span>
                  </>
                )}
              </div>
              <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
                Slice colors match the map overlay &mdash; a pink pixel on the
                map is the same Tribal ownership the pink slice counts.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Forested area" value={fmtAcres(data.forestAcres)} />
              <StatCard
                label="Largest ownership"
                value={`${largest.name.split(' /')[0]} (${largest.value.toFixed(0)}%)`}
              />
              <StatCard label="Non-forest area" value={fmtPct(nonForestFrac)} />
              <StatCard label="Water" value={fmtPct(waterFrac)} />
            </div>
          </>
        );
      })()}
    </div>
  );
};
